import { Button } from '../ui/button';
import { Pencil, Power, Key } from '@phosphor-icons/react';

const SECTOR_STYLE = {
  aviation: 'bg-blue-100 text-blue-800',
  maritime: 'bg-cyan-100 text-cyan-800',
  both: 'bg-purple-100 text-purple-800',
};
const SECTOR_LABEL = { aviation: 'Aviation', maritime: 'Maritime', both: 'Both' };
const USER_STATUS_STYLE = {
  approved: 'bg-green-100 text-green-800',
  active: 'bg-green-100 text-green-800',
  pending: 'bg-amber-100 text-amber-800',
};

export function PendingUsersTab({ pendingUsers, onApprove, onReject }) {
  return (
    <div className="bg-white rounded-xl p-6 border border-slate-200">
      <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Outfit' }}>Pending User Approvals</h2>
      {pendingUsers.length === 0 ? (
        <p className="text-slate-500 text-center py-8">No pending approvals</p>
      ) : (
        <div className="space-y-4">
          {pendingUsers.map((u) => (
            <div key={u.email} className="flex items-start justify-between p-4 border border-slate-200 rounded-lg hover:bg-slate-50">
              <div className="flex-1">
                <div className="font-semibold text-slate-900">{u.name}</div>
                <div className="text-sm text-slate-500">{u.email}</div>
                <div className="flex flex-wrap gap-2 mt-1.5">
                  <span className="text-xs text-slate-500 capitalize bg-slate-100 px-2 py-0.5 rounded">{u.role}</span>
                  {u.designation && <span className="text-xs text-slate-600 bg-blue-50 px-2 py-0.5 rounded">{u.designation}</span>}
                  {u.organization && <span className="text-xs text-slate-600 bg-purple-50 px-2 py-0.5 rounded">{u.organization}</span>}
                  {u.years_of_experience > 0 && <span className="text-xs text-slate-600 bg-green-50 px-2 py-0.5 rounded">{u.years_of_experience} yrs exp</span>}
                  {u.investigations_completed > 0 && <span className="text-xs text-slate-600 bg-amber-50 px-2 py-0.5 rounded">{u.investigations_completed} investigations</span>}
                  {u.primary_specialty && <span className="text-xs text-blue-700 bg-blue-100 px-2 py-0.5 rounded">{u.primary_specialty}</span>}
                </div>
                {u.certifications?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {u.certifications.slice(0, 3).map((c) => (
                      <span key={c} className="text-[10px] text-slate-500 border border-slate-200 px-1.5 py-0.5 rounded">{c}</span>
                    ))}
                    {u.certifications.length > 3 && <span className="text-[10px] text-slate-400">+{u.certifications.length - 3} more</span>}
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <Button onClick={() => onApprove(u.email)} className="bg-green-600 hover:bg-green-700 text-white rounded-lg">Approve</Button>
                <Button onClick={() => onReject(u.email)} variant="outline" className="border-red-300 text-red-600 hover:bg-red-50 rounded-lg">Reject</Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function AllUsersTab({ users, onEdit, onResetPassword, onToggleActive }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="p-6 border-b border-slate-200">
        <h2 className="text-xl font-semibold" style={{ fontFamily: 'Outfit' }}>All Users</h2>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Name</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Email</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Sector</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Role</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Experience</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Specialty</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.email} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3 text-sm text-slate-900">{u.name}</td>
                <td className="px-4 py-3 text-sm text-slate-600">{u.email}</td>
                <td className="px-4 py-3 text-sm">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${SECTOR_STYLE[u.sector] || SECTOR_STYLE.both}`}>
                    {SECTOR_LABEL[u.sector] || 'Both'}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-slate-600 capitalize">{u.role}</td>
                <td className="px-4 py-3 text-sm">
                  {u.years_of_experience ? (
                    <div>
                      <span className="font-medium text-slate-800">{u.years_of_experience} yrs</span>
                      {u.investigations_completed > 0 && <span className="text-xs text-slate-400 ml-1">/ {u.investigations_completed} inv</span>}
                    </div>
                  ) : <span className="text-slate-400">-</span>}
                </td>
                <td className="px-4 py-3 text-xs">
                  {u.primary_specialty ? (
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full">{u.primary_specialty}</span>
                  ) : u.specializations?.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {u.specializations.slice(0, 2).map((spec) => (
                        <span key={spec} className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full">{spec.split(' ')[0]}</span>
                      ))}
                      {u.specializations.length > 2 && <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full">+{u.specializations.length - 2}</span>}
                    </div>
                  ) : <span className="text-slate-400">-</span>}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${USER_STATUS_STYLE[u.status] || 'bg-red-100 text-red-800'}`}>{u.status}</span>
                    {u.is_active === false && <span className="px-2 py-0.5 bg-slate-200 text-slate-700 text-xs rounded">Inactive</span>}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => onEdit(u)} className="rounded-lg" title="Edit user"><Pencil size={14} /></Button>
                    <Button size="sm" variant="outline" onClick={() => onResetPassword(u.email)} className="rounded-lg text-amber-600" title="Reset password" data-testid={`reset-pw-${u.email}`}><Key size={14} /></Button>
                    <Button size="sm" variant="outline" onClick={() => onToggleActive(u.email, u.is_active)} className={`rounded-lg ${u.is_active === false ? 'text-green-600' : 'text-red-600'}`}><Power size={14} /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
